/**
 * Holdback adjustments — a client refund taken out of the trade's holdback BEFORE the final
 * release (Max, 2026-09-22: "a partial holdback refund made before the final release must
 * shrink the trade's final release, with notes").
 *
 * How the money moves: the client pays the platform the full job price. The trade's share
 * (trade net + the trade's own, registration-aware GST) is paid in two transfers, the initial
 * release and the final release (the holdback). Until the final release the holdback sits on
 * the platform balance. An admin can refund part or all of it to the client first
 * (`admin-refund-payout`, payout_type HELD_FINAL → a negative HELD_REFUND payout_transactions
 * row). The trade never received that money, so nothing is clawed back from it; instead the
 * final release pays the holdback LESS what was refunded, never below zero.
 *
 * Only the part of a refund the PLATFORM funded is taken off the holdback. A refund that was
 * (also) recovered from the trade — a Stripe reversal of one of its transfers, or a fallback
 * trade deduction — has already cost the trade that money once; taking it off the holdback
 * as well would charge the trade twice. (Before PR #3260 a held refund could reverse the
 * trade's INITIAL transfer; since #3260 a pre-release held refund cannot claw back at all.)
 *
 * Refunds made AFTER the final release are never taken off it: they are recovered by
 * reversing the FINAL_RELEASE transfer itself.
 *
 * Zero dependencies: shared by the Deno edge function that pays the final release
 * (handle-admin-approval) and by the web / mobile views that show what is owed and why.
 */

/** The payout_transactions columns this module reads. */
export interface HoldbackPayoutRow {
  id?: string | null;
  transaction_type: string;
  amount_cents: number | null;
  gst_amount_cents?: number | null;
  status?: string | null;
  created_at?: string | null;
  stripe_transfer_id?: string | null;
  metadata?: unknown;
}

/** One refund taken off the holdback, as recorded on the FINAL_RELEASE row. */
export interface HoldbackRefundDeduction {
  /** payout_transactions.id of the HELD_REFUND row. */
  payout_transaction_id: string | null;
  /** The Stripe refund id (re_…), which the HELD_REFUND row stores in stripe_transfer_id. */
  refund_id: string | null;
  refunded_at: string | null;
  reason: string | null;
  /** What the client got back (positive cents). */
  refund_cents: number;
  /** The part of it already recovered from the trade (reversal / deduction). */
  recovered_from_trade_cents: number;
  /** What this refund took off the holdback, inc GST. */
  deducted_cents: number;
  /** The GST part of deducted_cents. */
  deducted_gst_cents: number;
}

export interface HoldbackAdjustment {
  /** What the final release would have paid with no refunds, inc GST. */
  original_holdback_cents: number;
  original_holdback_gst_cents: number;
  /** Total taken off, inc GST, and its GST part. */
  deducted_cents: number;
  deducted_gst_cents: number;
  /** What the final release pays, inc GST, and its GST part. */
  final_release_cents: number;
  final_release_gst_cents: number;
  /** The refunds that took something off the holdback, oldest first. */
  refunds: HoldbackRefundDeduction[];
}

const REASON_MAX_LENGTH = 200;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return parseInt(value.trim(), 10);
  return null;
}

function toNonNegativeInt(value: unknown): number {
  const n = toInt(value);
  return n != null && n > 0 ? n : 0;
}

function timeOf(iso: string | null | undefined): number {
  if (!iso) return NaN;
  return Date.parse(iso);
}

/**
 * How much of a HELD_REFUND row was recovered from the trade rather than funded by the
 * platform. `metadata.reversals_by_transfer` (written by admin-refund-payout since it began
 * recording refunds) is the authoritative per-transfer breakdown of what was reversed; an
 * empty breakdown means nothing was. A fallback trade deduction (`deduction_created`) charges
 * the trade for the rest of the refund. A row that claims a clawback but carries no breakdown
 * is treated as fully recovered, so an unreadable row can never charge the trade twice.
 */
export function heldRefundRecoveredFromTradeCents(row: HoldbackPayoutRow): number {
  const refundCents = Math.abs(row.amount_cents ?? 0);
  const meta = asRecord(row.metadata);
  if (meta.deduction_created === true) return refundCents;

  const breakdown = meta.reversals_by_transfer;
  if (breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)) {
    const reversed = Object.values(breakdown as Record<string, unknown>).reduce<number>(
      (sum, v) => sum + Math.abs(toInt(v) ?? 0),
      0,
    );
    return Math.min(refundCents, reversed);
  }
  return meta.clawback_executed === true ? refundCents : 0;
}

/**
 * Take the platform-funded part of every COMPLETED held refund made before the final release
 * off the holdback.
 *
 * @param holdbackCents     what the final release would pay with no refunds (inc GST)
 * @param holdbackGstCents  its GST part (0 for a trade that is not GST-registered)
 * @param rows              the job's payout_transactions rows (any types)
 * @param finalReleasedAt   when a final release already exists: refunds from then on are
 *                          post-release and are not taken off. Omit while computing the
 *                          release itself (there is no final release yet).
 *
 * GST: a HELD_REFUND row records gst_amount_cents 0 — the refund row carries no GST split of
 * its own — so the deduction is split in the holdback's own GST ratio. That ratio comes from
 * the job's registration-aware trade GST (trade_gst_payout_cents), so a trade that is not
 * GST-registered has 0 GST deducted and no flat 10% is assumed. Should a refund row ever carry
 * a GST figure, that recorded figure is used for it instead. The deducted GST never exceeds
 * the holdback's GST, and a holdback refunded in full leaves 0 net and 0 GST.
 */
export function computeHoldbackAdjustment(input: {
  holdbackCents: number;
  holdbackGstCents: number;
  rows: readonly HoldbackPayoutRow[] | null | undefined;
  finalReleasedAt?: string | null;
}): HoldbackAdjustment {
  const holdback = Math.max(0, Math.round(input.holdbackCents || 0));
  const holdbackGst = Math.min(holdback, Math.max(0, Math.round(input.holdbackGstCents || 0)));
  const finalAt = timeOf(input.finalReleasedAt ?? null);

  const refundRows = (input.rows ?? [])
    .filter((r) =>
      r.transaction_type === 'HELD_REFUND' &&
      (r.status == null || r.status === 'COMPLETED') &&
      Math.abs(r.amount_cents ?? 0) > 0
    )
    .filter((r) => {
      if (input.finalReleasedAt == null) return true;
      // A final release exists: only refunds strictly before it came out of the holdback.
      // An undatable row is treated as post-release (not taken off), so it cannot charge
      // the trade a second time.
      const at = timeOf(r.created_at);
      return Number.isFinite(at) && Number.isFinite(finalAt) && at < finalAt;
    })
    .slice()
    .sort((a, b) => {
      const ta = timeOf(a.created_at);
      const tb = timeOf(b.created_at);
      return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
    });

  const refunds: HoldbackRefundDeduction[] = [];
  let deducted = 0;
  let deductedGst = 0;

  for (const row of refundRows) {
    const refundCents = Math.abs(row.amount_cents ?? 0);
    const recovered = heldRefundRecoveredFromTradeCents(row);
    const platformFunded = Math.max(0, refundCents - recovered);
    const take = Math.min(platformFunded, holdback - deducted);
    if (take <= 0) continue;

    const recordedGst = Math.abs(row.gst_amount_cents ?? 0);
    let takeGst = recordedGst > 0
      ? Math.round((recordedGst * take) / refundCents)
      : (holdback > 0 ? Math.round((take * holdbackGst) / holdback) : 0);
    takeGst = Math.max(0, Math.min(takeGst, take, holdbackGst - deductedGst));

    deducted += take;
    deductedGst += takeGst;

    const meta = asRecord(row.metadata);
    const reason = typeof meta.reason === 'string' && meta.reason.trim()
      ? meta.reason.trim().slice(0, REASON_MAX_LENGTH)
      : null;
    refunds.push({
      payout_transaction_id: row.id ?? null,
      refund_id: row.stripe_transfer_id ?? null,
      refunded_at: row.created_at ?? null,
      reason,
      refund_cents: refundCents,
      recovered_from_trade_cents: recovered,
      deducted_cents: take,
      deducted_gst_cents: takeGst,
    });
  }

  // A holdback refunded in full is paid as nothing at all: no GST left over from rounding.
  if (deducted === holdback && refunds.length > 0 && deductedGst !== holdbackGst) {
    refunds[refunds.length - 1].deducted_gst_cents += holdbackGst - deductedGst;
    deductedGst = holdbackGst;
  }

  const finalRelease = holdback - deducted;
  const finalReleaseGst = Math.max(0, Math.min(finalRelease, holdbackGst - deductedGst));

  return {
    original_holdback_cents: holdback,
    original_holdback_gst_cents: holdbackGst,
    deducted_cents: deducted,
    deducted_gst_cents: deductedGst,
    final_release_cents: finalRelease,
    final_release_gst_cents: finalReleaseGst,
    refunds,
  };
}

/**
 * The adjustment a FINAL_RELEASE row recorded in `metadata.holdback_adjustment`, or null when
 * it recorded none (a full holdback release, or a release made before adjustments existed).
 */
export function readHoldbackAdjustment(metadata: unknown): HoldbackAdjustment | null {
  const raw = asRecord(asRecord(metadata).holdback_adjustment);
  const deducted = toInt(raw.deducted_cents);
  const original = toInt(raw.original_holdback_cents);
  if (deducted == null || original == null || deducted <= 0) return null;

  const refunds = Array.isArray(raw.refunds)
    ? raw.refunds.map((r): HoldbackRefundDeduction => {
      const e = asRecord(r);
      return {
        payout_transaction_id: typeof e.payout_transaction_id === 'string' ? e.payout_transaction_id : null,
        refund_id: typeof e.refund_id === 'string' ? e.refund_id : null,
        refunded_at: typeof e.refunded_at === 'string' ? e.refunded_at : null,
        reason: typeof e.reason === 'string' ? e.reason : null,
        refund_cents: toNonNegativeInt(e.refund_cents),
        recovered_from_trade_cents: toNonNegativeInt(e.recovered_from_trade_cents),
        deducted_cents: toNonNegativeInt(e.deducted_cents),
        deducted_gst_cents: toNonNegativeInt(e.deducted_gst_cents),
      };
    })
    : [];

  return {
    original_holdback_cents: original,
    original_holdback_gst_cents: toNonNegativeInt(raw.original_holdback_gst_cents),
    deducted_cents: deducted,
    deducted_gst_cents: toNonNegativeInt(raw.deducted_gst_cents),
    final_release_cents: toNonNegativeInt(raw.final_release_cents),
    final_release_gst_cents: toNonNegativeInt(raw.final_release_gst_cents),
    refunds,
  };
}

/**
 * What has been (or, before the final release, will be) taken off a job's holdback.
 *
 * Once a COMPLETED FINAL_RELEASE exists, its recorded adjustment is the answer — it is what
 * was actually paid; a final release that recorded none took nothing off (it paid the full
 * holdback). Before the final release it is computed from the held refunds so far.
 */
export function resolveHoldbackAdjustment(input: {
  holdbackCents: number;
  holdbackGstCents: number;
  rows: readonly HoldbackPayoutRow[] | null | undefined;
}): HoldbackAdjustment {
  const finals = (input.rows ?? []).filter(
    (r) => r.transaction_type === 'FINAL_RELEASE' && (r.status == null || r.status === 'COMPLETED'),
  );
  if (finals.length === 0) {
    return computeHoldbackAdjustment({ ...input, finalReleasedAt: null });
  }
  for (const f of finals) {
    const recorded = readHoldbackAdjustment(f.metadata);
    if (recorded) return recorded;
  }
  const holdback = Math.max(0, Math.round(input.holdbackCents || 0));
  const holdbackGst = Math.min(holdback, Math.max(0, Math.round(input.holdbackGstCents || 0)));
  return {
    original_holdback_cents: holdback,
    original_holdback_gst_cents: holdbackGst,
    deducted_cents: 0,
    deducted_gst_cents: 0,
    final_release_cents: holdback,
    final_release_gst_cents: holdbackGst,
    refunds: [],
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "21 Sep 2026" in Sydney time (month names fixed here, so the text does not vary by ICU). */
export function formatHoldbackNoteDate(iso: string | null | undefined): string | null {
  const t = timeOf(iso);
  if (!Number.isFinite(t)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(t));
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const day = get('day');
    const month = Number(get('month'));
    const year = get('year');
    if (day && year && month >= 1 && month <= 12) return `${Number(day)} ${MONTHS[month - 1]} ${year}`;
  } catch {
    // fall through to the UTC date
  }
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function dollars(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const frac = (abs % 100).toString().padStart(2, '0');
  return `${negative ? '-' : ''}$${whole}.${frac}`;
}

/**
 * The one-line explanation, e.g.
 * "Holdback $500.92 − refund to client $200.00 (Faulty part, 21 Sep 2026) = $300.92".
 * Returns null when nothing was taken off.
 *
 * audience 'trade' (the default — remittances, the FINAL_RELEASE row, emails) shows only what
 * each refund took off the holdback, never the client's total refund: a refund larger than the
 * holdback is platform money the trade has no business seeing. audience 'admin' adds the total
 * ("$500.92 of the $1,252.31 refund to client") where it differs.
 */
export function formatHoldbackAdjustmentNote(
  adjustment: HoldbackAdjustment | null | undefined,
  options: { audience?: 'trade' | 'admin' } = {},
): string | null {
  if (!adjustment || adjustment.deducted_cents <= 0 || adjustment.refunds.length === 0) return null;
  const forAdmin = options.audience === 'admin';
  const terms = adjustment.refunds
    .filter((r) => r.deducted_cents > 0)
    .map((r) => {
      const context = [r.reason, formatHoldbackNoteDate(r.refunded_at)].filter(Boolean).join(', ');
      const suffix = context ? ` (${context})` : '';
      return forAdmin && r.deducted_cents < r.refund_cents
        ? `${dollars(r.deducted_cents)} of the ${dollars(r.refund_cents)} refund to client${suffix}`
        : `refund to client ${dollars(r.deducted_cents)}${suffix}`;
    });
  return `Holdback ${dollars(adjustment.original_holdback_cents)} − ${terms.join(' − ')} = ${
    dollars(adjustment.final_release_cents)
  }`;
}

/** The note for a payout_transactions row, from its metadata (FINAL_RELEASE rows only). */
export function holdbackAdjustmentNoteForPayout(row: {
  transaction_type?: string | null;
  metadata?: unknown;
}): string | null {
  if (row.transaction_type !== 'FINAL_RELEASE') return null;
  return formatHoldbackAdjustmentNote(readHoldbackAdjustment(row.metadata));
}
